import { legalNameDisplay } from './legalNameDisplay.ts';

// The vendor list's Vendor-cell name. Reuses legalNameDisplay's headline —
// Task 19's rule that the ABR registry name is authoritative for display on a
// genuine mismatch — so the list can never show a different name from the
// detail page's "Legal entity name" row. company_name is the final fallback:
// a blank Vendor cell would be worse than a duplicate of Trading Name.
export function vendorListName(
  abnEntityName: string | null,
  legalEntityName: string | null,
  companyName: string,
): string {
  const { headline } = legalNameDisplay(abnEntityName, legalEntityName);
  return headline?.trim() ? headline : companyName;
}

/** The three columns vendorListName reads — all the comparator needs. */
export type VendorListNameFields = {
  abn_entity_name: string | null;
  legal_entity_name: string | null;
  company_name: string;
};

// Array.prototype.sort comparator for the vendor list: A→Z on the name the
// Vendor cell actually SHOWS, so the order can never disagree with the column.
// Sorting on any single field would strand rows whose name comes from a
// different fallback tier.
//
// sensitivity:'base' makes it case-insensitive AND accent-folding, so 'acme'
// isn't exiled after 'Zebra' and 'Émile' isn't exiled after 'Zeta'. Ties fall
// back to Trading Name (company_name): two vendors can legitimately share a
// registry name, and without the tie-break their relative order would depend on
// the engine's sort stability and the server's updated_at order — i.e. it would
// shuffle as rows are edited.
const collate = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

export function compareByVendorListName(a: VendorListNameFields, b: VendorListNameFields): number {
  const byName = collate(
    vendorListName(a.abn_entity_name, a.legal_entity_name, a.company_name),
    vendorListName(b.abn_entity_name, b.legal_entity_name, b.company_name),
  );
  return byName !== 0 ? byName : collate(a.company_name, b.company_name);
}
